import * as d3 from 'd3-selection'
import { interpolate } from 'd3-interpolate'
import Splitter from '../../path/splitter'
import * as shape from 'd3-shape'

const lineGenerator = shape.line()

/*
 * Transforms an area path into a bunch of sub paths that represents a bar.
 */
class AreaBarTransformer {

  constructor (options) {
    this.serie = options.serie // The serie we're transforming

    let points = this.serie.selectAll('.point').data() // Old point data

    this.splitter = new Splitter(points, this.serie.selectAll('path.area'), options.splitWidth, options.splitScale, {
      x: options.previous.scalers.x,
      yScale: options.previous.yScale
    }, {
      yScale: options.yScale
    }, options.areaScale, true)

    this.interpolaters = {
      stroke: interpolate(options.styles.area.stroke, options.styles.bar.stroke),
      lineStrokeWidth: interpolate(options.styles.area.lineStrokeWidth, options.styles.bar.lineStrokeWidth),
      areaStrokeWidth: interpolate(options.styles.area.areaStrokeWidth, options.styles.bar.areaStrokeWidth),
      fill: interpolate(options.styles.area.fill, options.styles.bar.fill),
      opacity: interpolate(options.styles.area.opacity, options.styles.bar.opacity)
    }
  }

  /*
   * Inserts the split series into the DOM
   *
   * Can pass in a timing, `t`, which will be passed to the interpolators to
   * set the initial state. e.g. passing in `0`, the default will start the
   * animation as an area chart. Passing in the final timing, i.e. `1`, will
   * draw the series as a bar.
   */
  insert (t = 0) {
    let area = this.serie
      , cloned = area.node().cloneNode(true) // Clone the original before we remove it
      , splitter = this.splitter
      , newData = area.datum()
      , self = this

    // We're about the cover the path with a bunch of slivers that make up the whole thing
    let splits = d3.select(area.node().parentNode)
                   .selectAll(`g.serie-transitioning.serie-${area.datum().id}`)
                   .data(this.splitter.getSplits())
                   .enter()
                   .select(function (d) {
                      let node = cloned.cloneNode(true)

                      d3.select(node)
                        .datum(d)
                        .classed('serie-transitioning', true)
                        .classed(`serie-${area.datum().id}`, true)
                        .style('display', '')
                        .selectAll('.point').remove() // Don't show the points for this segment

                      // Set up interpolators for the path on each of the splits
                      let newPoint = newData.points.filter(p => p.rangeId === d._data.rangeId)[0]
                        , scaledTop = splitter.scalePoints(d.curves.top.pathPoints, newPoint, newData._columnIdx)
                        , scaledBottom = splitter.scalePoints(d.curves.bottom.pathPoints, newPoint, newData._columnIdx, true, (x, width) => {
                          return [x + width, x]
                        })

                      d.interpolateLine = interpolate(lineGenerator(d.curves.top.pathPoints), lineGenerator(scaledTop))
                      d.interpolateArea = interpolate(lineGenerator(d.curves.top.pathPoints.concat(d.curves.bottom.pathPoints)),
                                                      lineGenerator(scaledTop.concat(scaledBottom)))

                      d3.select(node)
                        .selectAll('path.line').attr('d', d.interpolateLine(t))
                        .style('display', '')

                      d3.select(node)
                        .selectAll('path.area').attr('d', d.interpolateArea(t) + 'Z')
                        .style('display', '')
                        .style('opacity', self.interpolaters.opacity(t))
                        .style('stroke-width', self.interpolaters.areaStrokeWidth(t))
                        .style('stroke', self.interpolaters.stroke(t))

                      this.insertBefore(node, area.node())

                      return node
                   })

    return splits
  }

  /*
   * Transforms the node, `node` using the set interpolators. `t` is a value
   * in [0, 1], where the 0 state is for an area chart and `1` is a bar chart.
   */
  transform (t, node) {
    let d = d3.select(node).datum()

    d3.select(node)
      .selectAll('path.line')
      .style('stroke-width', this.interpolaters.lineStrokeWidth(t))
      .attr('d', d.interpolateLine(t))

    d3.select(node)
      .selectAll('path.area')
      .style('fill', this.interpolaters.fill(t))
      .style('stroke-width', this.interpolaters.areaStrokeWidth(t))
      .style('opacity', this.interpolaters.opacity(t))
      .style('stroke', this.interpolaters.stroke(t))
      .attr('d', d.interpolateArea(t) + 'Z') // Z to close the path
  }

}

export default AreaBarTransformer
